/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { shape, string, bool, object, func } from 'prop-types';
import { Link, Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import p from 'polyglot/polyglot';

import { createUrl } from 'utils/urlHelper';

import {
  FB_AUTH_SUCCESS,
  G_AUTH_SUCCESS,
  AUTH_SUCCESS,
  authenticate as authenticateAction,
  googleAuth as googleAuthAction,
  facebookAuth as facebookAuthAction,
} from 'actions/auth';

import { hideLoginModal as hideLoginModalAction } from 'actions/login';
import { showSignupModal as showSignupModalAction } from 'actions/signup';

import { SIGNUP_STATUS } from 'reducers/user';

import ModalWrapper from 'components/ModalWrapper/ModalWrapper';
import AuthButton from 'components/AuthButton/AuthButton';
import EmailPasswordForm from 'components/EmailPasswordForm/EmailPasswordForm';

import styles from './login-modal.scss';

/**
 * This is the LoginModal component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class LoginModal extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    history: object.isRequired,
    location: object.isRequired,
    user: object.isRequired,
    login: shape({
      showModal: bool.isRequired,
    }).isRequired,
    auth: shape({
      asUser: string,
      isAuthenticated: bool.isRequired,
    }).isRequired,
    authenticate: func.isRequired,
    facebookAuth: func.isRequired,
    googleAuth: func.isRequired,
    showSignupModal: func.isRequired,
    hideLoginModal: func.isRequired,
  };

  componentDidUpdate(prevProps) {
    const { location: prevLocation, login: prevLogin } = prevProps;
    const { location, login, hideLoginModal } = this.props;

    if (prevLocation.pathname !== location.pathname && login.showModal) {
      hideLoginModal();
    }

    if (prevLogin.showModal !== login.showModal) {
      const intercomApp = document.querySelector('.intercom-app');

      if (intercomApp) {
        intercomApp.style.visibility = login.showModal ? 'hidden' : 'visible';
      }
    }
  }

  onClickSignUp = event => {
    event.preventDefault();

    const { showSignupModal } = this.props;

    showSignupModal();
  };

  // Duplicate code in signup component.
  fbAuth = () => {
    const { facebookAuth } = this.props;

    facebookAuth().then(({ type, payload }) => {
      if (type === FB_AUTH_SUCCESS) {
        this.authenticate(payload);
      } else {
        console.log('SOMETHING WENT WRONG WITH THE FACEBOOK REQUEST!');
      }
    });
  };

  // Duplicate code in signup component.
  gAuth = () => {
    const { googleAuth } = this.props;

    googleAuth().then(({ type, payload }) => {
      if (type === G_AUTH_SUCCESS) {
        this.authenticate(payload);
      } else {
        console.log('SOMETHING WENT WRONG WITH THE GOOGLE REQUEST!');
      }
    });
  };

  // Almost duplicate code in signup component.
  authenticate = data => {
    const { auth, history, authenticate } = this.props;

    return new Promise(resolve => {
      authenticate({
        ...data,
        as_user: auth.asUser,
        language: 'en', // TODO: What locale is to be used?
      }).then(({ type, payload }) => {
        resolve({ type });
        if (type === AUTH_SUCCESS) {
          if (payload.signup_status === SIGNUP_STATUS.APPLICATION_APPROVED) {
            history.push(createUrl('account'));
          } else {
            history.push(createUrl('signup'));
          }
        }
      });
    });
  };

  /**
   * Render LoginModal.
   *
   * @return {Object}
   */
  render() {
    const {
      user,
      auth,
      location,
      login: { showModal },
      hideLoginModal,
    } = this.props;
    const { from } = location.state || {
      from: { pathname: '/' },
    };

    if (!showModal) return null;

    if (auth.isAuthenticated) {
      if (user.status === SIGNUP_STATUS.APPLICATION_APPROVED) {
        // User is approved.
        return <Redirect to={from} />;
      } else {
        // User needs to complete signup.
        return <Redirect to="/signup" />;
      }
    }

    return (
      <ModalWrapper wide hideModal={hideLoginModal}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <h2 className={styles.title}>{p.t('login.title')} </h2>
          </div>

          <div className={styles.passwordForm}>
            <EmailPasswordForm wide authenticate={this.authenticate} />
          </div>

          <div className={styles.or}>
            <div className={styles.divider}></div>
            <span className={styles.dividerName}>or</span>
            <div className={styles.divider}></div>
          </div>

          <div className={styles.someWrapper}>
            <ul className={styles.menu}>
              <li>
                <AuthButton
                  onClick={this.fbAuth}
                  type="facebook"
                  showIcon={true}
                  wide
                >
                  Facebook
                </AuthButton>
              </li>
              <li>
                <AuthButton
                  onClick={this.gAuth}
                  type="google"
                  showIcon={true}
                  wide
                >
                  Google
                </AuthButton>
              </li>
            </ul>

            <p className={styles.content}>
              {p.t('login.reset_text')}
              <Link to={createUrl('passwordReset')}>
                {p.t('login.reset_link')}
              </Link>
            </p>
          </div>
        </div>
      </ModalWrapper>
    );
  }
}

export default withRouter(
  connect(({ user, auth, login }) => ({ user, auth, login }), {
    authenticate: authenticateAction,
    googleAuth: googleAuthAction,
    facebookAuth: facebookAuthAction,
    hideLoginModal: hideLoginModalAction,
    showSignupModal: showSignupModalAction,
  })(LoginModal)
);
